import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const TeamCreationForm = ({ onTeamCreated, onCancel, defaultOrganizationId = null, organizations = [] }) => {
  const { user } = useAuth();
  const authToken = user?.token || localStorage.getItem('token');
  const [formData, setFormData] = useState({
    name: '',
    sport: 'baseball',
    ageGroup: '',
    age_group_id: null,
    season: '',
    description: '',
    maxPlayers: 20,
    primaryColor: '#2563eb',
    secondaryColor: '#1d4ed8'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdTeam, setCreatedTeam] = useState(null);
  const [availableAgeGroups, setAvailableAgeGroups] = useState([]);
  const [ageGroupsLoading, setAgeGroupsLoading] = useState(true);
  const [selectedAgeGroupKey, setSelectedAgeGroupKey] = useState('');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(defaultOrganizationId || '');
  const [availablePrograms, setAvailablePrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState('');

  // Keep selected org in sync when defaultOrganizationId is set (e.g. after orgs load)
  useEffect(() => {
    if (defaultOrganizationId && !selectedOrganizationId) {
      setSelectedOrganizationId(defaultOrganizationId);
    }
  }, [defaultOrganizationId]);

  useEffect(() => {
    const fetchAgeGroups = async () => {
      try {
        setAgeGroupsLoading(true);
        const params = {};
        const orgId = selectedOrganizationId || defaultOrganizationId;
        if (orgId) {
          params.organizationId = orgId;
        }

        const response = await axios.get('/api/age-groups', {
          params,
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
        });
        const rawGroups = Array.isArray(response.data) ? response.data : response.data?.ageGroups || [];
        const normalized = rawGroups
          .map(group => {
            if (!group) return null;
            // Handle both camelCase and snake_case payloads
            const id = group.id ?? group.age_group_id ?? null;
            const name = group.name || group.ageGroup || '';
            const organizationId = group.organization_id ?? group.organizationId ?? null;
            const isActive = group.is_active ?? group.isActive ?? true;
            const source = group.source || 'configured';

            if (!name) return null;

            return { id, name, organization_id: organizationId, is_active: isActive, source };
          })
          .filter(Boolean)
          .filter(group => group.is_active !== false);

        setAvailableAgeGroups(normalized);
      } catch (error) {
        console.error('Error fetching age groups for team creation:', error);
      } finally {
        setAgeGroupsLoading(false);
      }
    };

    fetchAgeGroups();
  }, [authToken, defaultOrganizationId, selectedOrganizationId]);

  useEffect(() => {
    const orgId = selectedOrganizationId || defaultOrganizationId;
    if (!orgId) { setAvailablePrograms([]); return; }
    axios.get(`/api/programs?organization_id=${orgId}&active=true`, {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
    }).then(r => setAvailablePrograms(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAvailablePrograms([]));
  }, [authToken, selectedOrganizationId, defaultOrganizationId]);

  const ageGroupOptions = useMemo(() => {
    return (availableAgeGroups || [])
      .filter(group => group?.name)
      .map(group => ({
        key: group.id ? `id:${group.id}` : `name:${group.name}`,
        label: group.name,
        meta: group
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [availableAgeGroups]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAgeGroupSelect = (value) => {
    setSelectedAgeGroupKey(value);

    if (!value) {
      setFormData(prev => ({
        ...prev,
        ageGroup: '',
        age_group_id: null
      }));
      return;
    }

    if (value === '__custom__') {
      setFormData(prev => ({
        ...prev,
        ageGroup: '',
        age_group_id: null
      }));
      return;
    }

    if (value.startsWith('id:')) {
      const id = parseInt(value.slice(3), 10);
      const selected = ageGroupOptions.find(option => option.key === value);
      setFormData(prev => ({
        ...prev,
        ageGroup: selected?.label || '',
        age_group_id: Number.isNaN(id) ? null : id
      }));
      return;
    }

    if (value.startsWith('name:')) {
      const name = value.slice(5);
      setFormData(prev => ({
        ...prev,
        ageGroup: name,
        age_group_id: null
      }));
      return;
    }

    // Fallback
    setFormData(prev => ({
      ...prev,
      ageGroup: '',
      age_group_id: null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const trimmedCustomAgeGroup = (formData.ageGroup || '').trim();
      let resolvedAgeGroupId = formData.age_group_id ? parseInt(formData.age_group_id, 10) : null;
      let resolvedAgeGroupName = trimmedCustomAgeGroup;

      if (selectedAgeGroupKey && selectedAgeGroupKey !== '__custom__') {
        if (selectedAgeGroupKey.startsWith('id:')) {
          resolvedAgeGroupId = parseInt(selectedAgeGroupKey.slice(3), 10);
          const selected = ageGroupOptions.find(option => option.key === selectedAgeGroupKey);
          resolvedAgeGroupName = selected?.label || trimmedCustomAgeGroup;
        } else if (selectedAgeGroupKey.startsWith('name:')) {
          resolvedAgeGroupId = null;
          resolvedAgeGroupName = selectedAgeGroupKey.slice(5);
        }
      } else if (selectedAgeGroupKey === '__custom__') {
        if (!trimmedCustomAgeGroup) {
          toast.error('Please enter a custom age group label.');
          setIsSubmitting(false);
          return;
        }
        resolvedAgeGroupId = null;
        resolvedAgeGroupName = trimmedCustomAgeGroup;
      } else if (!selectedAgeGroupKey && !trimmedCustomAgeGroup) {
        resolvedAgeGroupId = null;
        resolvedAgeGroupName = '';
      }

      const orgId = selectedOrganizationId || defaultOrganizationId;
      const resolvedOrgId = orgId ? parseInt(orgId, 10) : null;
      if (user?.role === 'org_admin' && !resolvedOrgId) {
        toast.error('Please select an organization for this team.');
        setIsSubmitting(false);
        return;
      }

      const response = await axios.post('/api/teams', {
        name: formData.name,
        sport: formData.sport,
        ageGroup: resolvedAgeGroupName || null,
        age_group_id: resolvedAgeGroupId,
        organization_id: resolvedOrgId,
        program_id: selectedProgramId ? parseInt(selectedProgramId, 10) : null,
        season: formData.season,
        description: formData.description,
        maxPlayers: parseInt(formData.maxPlayers),
        colors: {
          primary: formData.primaryColor,
          secondary: formData.secondaryColor
        }
      }, {
        headers: {
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
        }
      });

      toast.success(`Team "${response.data.name}" created successfully!`);
      setCreatedTeam(response.data);
      
      if (onTeamCreated) {
        onTeamCreated(response.data);
      }
    } catch (error) {
      console.error('Error creating team:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to create team');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show success screen after team creation
  if (createdTeam) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Team Created Successfully!</h2>
          <p className="text-gray-600 mb-6">Your team "{createdTeam.name}" has been created.</p>
          
          {/* Join Code Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">Team Join Code</h3>
            <div className="bg-white border-2 border-blue-300 rounded-lg p-4 mb-4">
              <code className="text-3xl font-mono font-bold text-blue-900 tracking-wider">
                {createdTeam.teamCode}
              </code>
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(createdTeam.teamCode);
                toast.success('Join code copied to clipboard!');
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Copy Join Code
            </button>
          </div>

          {/* Important Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-yellow-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="text-left">
                <h4 className="text-sm font-medium text-yellow-800">Important:</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  You are not automatically added to the team. You must use the join code above to join your own team, just like any other member.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center space-x-3">
            <button
              onClick={() => {
                setCreatedTeam(null);
                setFormData({
                  name: '',
                  sport: 'baseball',
                  ageGroup: '',
                  age_group_id: null,
                  season: '',
                  description: '',
                  maxPlayers: 20,
                  primaryColor: '#2563eb',
                  secondaryColor: '#1d4ed8'
                });
                setSelectedAgeGroupKey('');
                setSelectedOrganizationId(defaultOrganizationId || '');
              }}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
            >
              Create Another Team
            </button>
            {onCancel && (
              <button
                onClick={onCancel}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Create New Team</h2>
        {onCancel && (
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Organization (when user has orgs - e.g. org_admin) */}
        {organizations.length > 0 && (
          <div>
            <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
              Organization {user?.role === 'org_admin' ? '*' : ''}
            </label>
            <select
              id="organization"
              value={selectedOrganizationId}
              onChange={(e) => setSelectedOrganizationId(e.target.value)}
              required={user?.role === 'org_admin'}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{user?.role === 'org_admin' ? 'Select organization...' : 'No organization'}</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}{org.organizationCode ? ` (${org.organizationCode})` : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Assign this team to an organization so it appears under the right org.
            </p>
          </div>
        )}

        {/* Program */}
        {availablePrograms.length > 0 && (
          <div>
            <label htmlFor="program" className="block text-sm font-medium text-gray-700 mb-2">
              Program
            </label>
            <select
              id="program"
              value={selectedProgramId}
              onChange={(e) => setSelectedProgramId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">No program</option>
              {availablePrograms.map(p => (
                <option key={p.id} value={p.id}>{p.name}{p.season ? ` — ${p.season}` : ''}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Linking a team to a program controls its visibility as the program starts and ends.
            </p>
          </div>
        )}

        {/* Team Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Team Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., Thunder Hawks, Lightning Bolts"
          />
        </div>

        {/* Sport and Age Group */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="sport" className="block text-sm font-medium text-gray-700 mb-2">
              Sport *
            </label>
            <select
              id="sport"
              name="sport"
              value={formData.sport}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="baseball">Baseball</option>
              <option value="softball">Softball</option>
              <option value="soccer">Soccer</option>
              <option value="basketball">Basketball</option>
              <option value="football">Football</option>
              <option value="hockey">Hockey</option>
              <option value="volleyball">Volleyball</option>
              <option value="tennis">Tennis</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label htmlFor="ageGroup" className="block text-sm font-medium text-gray-700 mb-2">
              Age Group
            </label>
            <select
              id="ageGroup"
              name="ageGroupSelect"
              value={selectedAgeGroupKey}
              onChange={(e) => handleAgeGroupSelect(e.target.value)}
              disabled={ageGroupsLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">No Age Group</option>
              {ageGroupOptions.map(option => (
                <option key={option.key} value={option.key}>
                  {option.label}{option.meta?.source === 'team' ? ' (from team data)' : ''}
                </option>
              ))}
              <option value="__custom__">Custom age group...</option>
            </select>
            {ageGroupsLoading && (
              <p className="mt-1 text-xs text-gray-500">Loading age groups...</p>
            )}
            {selectedAgeGroupKey === '__custom__' && (
              <input
                type="text"
                name="ageGroup"
                value={formData.ageGroup}
                onChange={handleInputChange}
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter custom age group label (e.g., 12U, Varsity)"
              />
            )}
          </div>
        </div>

        {/* Season */}
        <div>
          <label htmlFor="season" className="block text-sm font-medium text-gray-700 mb-2">
            Season
          </label>
          <input
            type="text"
            id="season"
            name="season"
            value={formData.season}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., Spring 2024, Fall 2024"
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Brief description of the team..."
          />
        </div>

        {/* Max Players */}
        <div>
          <label htmlFor="maxPlayers" className="block text-sm font-medium text-gray-700 mb-2">
            Maximum Players
          </label>
          <input
            type="number"
            id="maxPlayers"
            name="maxPlayers"
            value={formData.maxPlayers}
            onChange={handleInputChange}
            min="1"
            max="50"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Team Colors */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Team Colors
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="primaryColor" className="block text-xs text-gray-600 mb-1">
                Primary Color
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  id="primaryColor"
                  name="primaryColor"
                  value={formData.primaryColor}
                  onChange={handleInputChange}
                  className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.primaryColor}
                  onChange={handleInputChange}
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                  placeholder="#2563eb"
                />
              </div>
            </div>
            <div>
              <label htmlFor="secondaryColor" className="block text-xs text-gray-600 mb-1">
                Secondary Color
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  id="secondaryColor"
                  name="secondaryColor"
                  value={formData.secondaryColor}
                  onChange={handleInputChange}
                  className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.secondaryColor}
                  onChange={handleInputChange}
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                  placeholder="#1d4ed8"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting || !formData.name.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Creating...' : 'Create Team'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeamCreationForm;
